'use client'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, useInView } from 'motion/react'
import { Ticker, TickerItem, useTickerAnimation } from '@/components/Ticker/Ticker'
import type { ExpectedBlockType } from '@/services/storyblok/storyblok'
import { type SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { StoryblokComponent } from '@storyblok/react'
import { type FontSizes } from 'ui/src/theme/typography'
import type { HeadingBlockProps } from './HeadingBlock/HeadingBlock'
import type { TextBlockProps } from './TextBlock/TextBlock'

type Props = SbBaseBlockProps<{
  showCheckIcon?: boolean
  tickerItems?: ExpectedBlockType<HeadingBlockProps | TextBlockProps>
  tickerHeight: FontSizes
  tickerHeightDesktop?: FontSizes
}>

export const TickerBlock = ({ blok }: Props) => {
  const ref = useRef(null)
  const isInView = useInView(ref)
  const isBrowserTabVisible = useIsBrowserTabVisible()
  const [visibleIndex, setVisibleIndex] = useState(0)
  const tickerItems = blok.tickerItems ?? []
  const childrenCount = tickerItems.length
  const currentItem = [tickerItems[visibleIndex]]

  useTickerAnimation(() => {
    setVisibleIndex((prevIndex) => (prevIndex + 1) % childrenCount)
  }, isBrowserTabVisible && isInView)

  const sizes = {
    _: blok.tickerHeight,
    ...(blok.tickerHeightDesktop && { md: blok.tickerHeightDesktop }),
  }

  return (
    <div ref={ref}>
      <Ticker size={sizes}>
        <AnimatePresence initial={true}>
          {currentItem.map((nestedBlock) => (
            <TickerItem key={nestedBlock._uid} showCheckIcon={blok.showCheckIcon}>
              <StoryblokComponent blok={nestedBlock} nested={true}>
                {nestedBlock}
              </StoryblokComponent>
            </TickerItem>
          ))}
        </AnimatePresence>
      </Ticker>
    </div>
  )
}

function useIsBrowserTabVisible() {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible')
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  return isVisible
}
